import samlConfigurationStyle from "../saml-configuration.module.css";

import { useState, useReducer, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { cloneDeep } from "lodash";

// Hook
import useMessage from "hooks/useMessage";

// Const
import { organizationType } from "const/nuclias/organization";

// Slice
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

// UI
import SpinnerForModal from "ui/SpinnerForModal";

// Component
import Input from "components/Input";
import Button from "components/Button";
import ModalContainer from "components/ModalContainer";
import DropdownWithItem from "components/DropdownWithItem";
import MessageBoxGroup from "components/MessageBoxGroup";

// Model
import SamlRoleModel from "../models/SamlRole";

// Store
import samlRoleStore from "../stores/samlRole";

// Controller
import controller from "../controller";

const AddSamlRoleModal = props => {
  const {
    modalStatus,
    changeModalStatus,
    user,
    orgTreeMap,
    selectedOrg,
    selectedSamlRole = null,
    successCallback
  } = props;
  const { orgType } = selectedOrg;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Slice
  const showModalSpinner = useSelector(selectShowModalSpinner);

  // State
  const [samlRoleState, samlRoleDispatch] = useReducer(
    samlRoleStore.reducer,
    samlRoleStore.defaultState
  );

  // Hook
  const {
    messages,
    messagesType,
    resetMessages,
    setErrorMessage
  } = useMessage();

  // Variable
  const managedScopeForMSP = (
    <>
      <Col>
        <div className="modal-form-title required">{t('31de740e0b')}</div>
        <DropdownWithItem
          id="license-term-dropdown"
          type="normal"
          disabled={samlRoleState.managedOrganization.disabled}
          selectedItem={samlRoleState.managedOrganization.selected}
          itemList={samlRoleState.managedOrganization.list}
        />
      </Col>
    </>
  );

  const managedScopeForOrg = (
    <>
      <Col>
        <div className="modal-form-title required">{t('199e0bdbbb')}</div>
        <DropdownWithItem
          id="license-term-dropdown"
          type="normal"
          disabled={samlRoleState.managedSiteTag.disabled}
          selectedItem={samlRoleState.managedSiteTag.selected}
          itemList={samlRoleState.managedSiteTag.list}
          onClick={item => changeMangedSiteTag(item)}
        />
      </Col>
      <Col>
        <div className="modal-form-title">&nbsp;</div>
        <DropdownWithItem
          id="license-term-dropdown"
          type="normal"
          disabled={samlRoleState.managedSite.disabled}
          selectedItem={samlRoleState.managedSite.selected}
          itemList={samlRoleState.managedSite.list}
          onClick={item => changeManagedSite(item)}
        />
      </Col>
    </>
  );

  // Method
  const init = useCallback(() => {
    if (!selectedSamlRole) {
      samlRoleDispatch({
        type: samlRoleStore.action.initializeState,
        payload: SamlRoleModel.getNewSamlRoleState(user, selectedOrg)
      });
    } else {
      samlRoleDispatch({
        type: samlRoleStore.action.initializeState,
        payload: SamlRoleModel.getEditedSamlRoleState(user, orgTreeMap, selectedOrg, selectedSamlRole)
      });
    }

  }, [orgTreeMap, selectedOrg, selectedSamlRole, user]);

  const changeName = (value) => {
    const updatedName = cloneDeep(samlRoleState.name);
    SamlRoleModel.changeName(updatedName, value);

    samlRoleDispatch({
      type: samlRoleStore.action.changeName,
      payload: updatedName
    });
  }

  const changeAccessPrivilegeRole = (role) => {
    const updatedAccessPrivilegeRole = cloneDeep(samlRoleState.accessPrivilegeRole);
    SamlRoleModel.changeAccessPrivilegeRole(updatedAccessPrivilegeRole, role);

    samlRoleDispatch({
      type: samlRoleStore.action.changeAccessPrivilegeRole,
      payload: updatedAccessPrivilegeRole
    });
  }

  const changeMangedSiteTag = (item) => {
    const updatedManagedSiteTag = cloneDeep(samlRoleState.managedSiteTag);
    SamlRoleModel.changeManagedSiteTag(updatedManagedSiteTag, item);

    samlRoleDispatch({
      type: samlRoleStore.action.changeManagedSiteTag,
      payload: updatedManagedSiteTag
    });

    const updatedManagedSite = cloneDeep(samlRoleState.managedSite);
    SamlRoleModel.renewManagedSite(orgTreeMap, selectedOrg, updatedManagedSite, item);
    samlRoleDispatch({
      type: samlRoleStore.action.renewManagedSite,
      payload: updatedManagedSite
    });
  }

  const changeManagedSite = (item) => {
    const updatedManagedSite = cloneDeep(samlRoleState.managedSite);
    SamlRoleModel.changeManagedSite(updatedManagedSite, item);

    samlRoleDispatch({
      type: samlRoleStore.action.renewManagedSite,
      payload: updatedManagedSite
    });
  }

  const checkForm = () => {
    let state = true;
    for (const fieldKey in samlRoleState) {
      const { valid } = samlRoleState[fieldKey];
      if (!valid) {
        state = false;
      }
    }

    return state;
  }

  const addAndSaveSamlRole = async () => {
    dispatch(uiActions.showModalSpinner());

    try {
      const response = await controller.addOrSaveSamlRole(selectedOrg.id, selectedSamlRole?.id, samlRoleState);

      if (response.error) {
        throw response.error;
      }

      dispatch(uiActions.hideModalSpinner());
      resetMessages();
      changeModalStatus('addAndEdit', false);
      successCallback();
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideModalSpinner());
    }
  }

  // Side effect
  useEffect(() => {
    if (modalStatus.addAndEdit.status) {
      dispatch(uiActions.showModalSpinner());
      init();
      setTimeout(() => {
        dispatch(uiActions.hideModalSpinner());
      }, 1000);
    }
  }, [dispatch, init, modalStatus.addAndEdit.status]);

  return (
    <ModalContainer
      modalWidthType="modal-500px"
      openModal={modalStatus.addAndEdit.status}
      closeModal={() => changeModalStatus('addAndEdit', false)}
    >
      {showModalSpinner && <SpinnerForModal />}
      <div className="header">
        <div className="title">
          {!selectedSamlRole && t('6c1339355f')}
          {selectedSamlRole && t('8abe5a338f')}
        </div>
      </div>
      <div className="body">
        <MessageBoxGroup
          messages={messages}
          ErrorMessagesType={messagesType.error.server}
          onClose={resetMessages}
        />
        {/* Name */}
        <div className="mb-3">
          <div className="modal-form-title required">{t('18697449d7')}</div>
          <Input
            type="text"
            placeholder="7563cde047"
            value={samlRoleState.name.value}
            isInvalid={samlRoleState.name.dirty && !samlRoleState.name.valid}
            onChange={e => changeName(e.target.value)}
          />
        </div>

        {/* Access privilege */}
        <div className={'sub-title mb-3'}>{t('8f1bd444d2')}</div>
        <div className="mb-3">
          <div className="modal-form-title required">{t('727b677950')}</div>
          <DropdownWithItem
            id="used-unused-dropdown"
            type="normal"
            disabled={samlRoleState.accessPrivilegeRole.disabled}
            selectedItem={samlRoleState.accessPrivilegeRole.selected}
            itemList={samlRoleState.accessPrivilegeRole.list}
            onClick={changeAccessPrivilegeRole}
          />
        </div>
        <Row>
          <Col sm={6}>
            <div className={`mb-3 ${samlConfigurationStyle['modal-text']}`}>
              {t('de88250cdc')}
              <div className={samlConfigurationStyle['modal-access-level-label']}>
                {orgType === organizationType.si && t('4b65444060')}
                {orgType === organizationType.org && t('c1ca926603')}
              </div>
            </div>
          </Col>
          {orgType === organizationType.org && (
            <Col sm={6}>
              <div className={`mb-3 ${samlConfigurationStyle['modal-text']}`}>
                <div style={{ marginTop: 18 }}></div>
                <div className={samlConfigurationStyle['modal-access-level-label']}>
                  {selectedOrg.name}
                </div>
              </div>
            </Col>
          )}
        </Row>

        {/* Managed scope */}
        <div className="mb-2">
          <Row>
            {orgType === organizationType.si && managedScopeForMSP}
            {orgType === organizationType.org && managedScopeForOrg}
          </Row>
        </div>
      </div>

      <div className="footer">
        <Button
          label="4a53b84974"
          className="btn-cancel"
          onClick={() => {
            console.log('click close');
            changeModalStatus('addAndEdit', false);
          }}
        />
        <Button
          label="c9cc8cce24"
          className="btn-submit"
          disabled={!checkForm()}
          onClick={() => addAndSaveSamlRole()}
        />
      </div>
    </ModalContainer>
  );
};

export default AddSamlRoleModal;
